import { useState } from "react";
import { toast } from "sonner";
import { FileText, Link2, Loader2, MessageCircle, RefreshCw, Download, Clock } from "lucide-react";
import { frApi, errMsg, quoteMessage, quoteUrl, btnPrimary, btnGhost, fieldCls } from "./frUtils";

const waLink = (phone, text) => {
    const d = (phone || "").replace(/\D/g, "");
    return `https://wa.me/${d.length >= 10 ? (d.length === 10 ? `91${d}` : d) : ""}?text=${encodeURIComponent(text)}`;
};

/** Quotation is generated on demand; sharing always needs the employee's own tap (WhatsApp opens prefilled). */
export default function QuoteActions({ record, onRecord }) {
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState(record.quote && record.quote_token ? quoteMessage(record.quote, record.quote_token) : "");

    const refresh = async () => {
        setBusy(true);
        try { onRecord(await frApi.get(`/records/${record.id}`)); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
    };
    const generate = async () => {
        setBusy(true);
        try {
            const { token, quote } = await frApi.post(`/records/${record.id}/quote`);
            onRecord({ ...record, quote_token: token, quote });
            setMsg(quoteMessage(quote, token));
            toast.success("Quotation ready. Review it before sharing.");
        } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
    };

    if (record.approval_status === "pending" || record.approval_status === "rejected") {
        return (
            <div className="flex flex-wrap items-center gap-2 text-sm" data-testid="fr-quote-blocked">
                <span className="inline-flex items-center gap-1.5 text-amber-800"><Clock className="w-4 h-4" />
                    {record.approval_status === "pending" ? "Waiting for management approval before a quotation can be shared." : "Management rejected this fee. Revise it in History."}</span>
                <button type="button" onClick={refresh} disabled={busy} className={btnGhost} data-testid="fr-refresh-approval"><RefreshCw className="w-4 h-4" /> Check status</button>
            </div>
        );
    }
    if (!record.quote_token) {
        return (
            <button type="button" onClick={generate} disabled={busy} className={`${btnPrimary} w-full`} data-testid="fr-generate-quote-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />} Generate Fee Quotation
            </button>
        );
    }
    const url = quoteUrl(record.quote_token);
    return (
        <div className="space-y-2" data-testid="fr-quote-ready">
            <textarea value={msg} onChange={(e) => setMsg(e.target.value)} rows={8} className={`${fieldCls} !h-auto py-2 font-mono text-xs`} data-testid="fr-quote-message" />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <a href={waLink(record.phone, msg)} target="_blank" rel="noreferrer" className={`${btnPrimary} !bg-[#25D366] hover:!bg-[#1EBE5A]`} data-testid="fr-quote-whatsapp">
                    <MessageCircle className="w-4 h-4" /> Open WhatsApp
                </a>
                <button type="button" className={btnGhost} data-testid="fr-quote-copy-link"
                    onClick={() => navigator.clipboard.writeText(url).then(() => toast.success("Link copied")).catch(() => toast.error("Copy failed"))}>
                    <Link2 className="w-4 h-4" /> Copy link
                </button>
                <a href={`${url}?print=1`} target="_blank" rel="noreferrer" className={btnGhost} data-testid="fr-quote-pdf"><Download className="w-4 h-4" /> Download PDF</a>
            </div>
            <p className="text-[11px] text-slate-500">WhatsApp opens with this message filled in. Nothing is sent until you tap send there.</p>
        </div>
    );
}
